import React, { useState, useRef, useEffect } from 'react';

interface Message {
  role: 'user' | 'ai';
  text: string;
  intent?: string;
  timestamp: string;
}

const SUGGESTED = [
  'Compare Jordan and LeBron era-adjusted',
  'Simulate Bulls vs Lakers playoff series',
  'Who had the better playoffs — Kobe or LeBron?',
  'Normalize Wilt Chamberlain stats to modern era'
];

const API_URL = process.env.REACT_APP_API_URL;

const Dashboard: React.FC = () => {
  const [messages, setMessages]   = useState<Message[]>([
    {
      role: 'ai',
      text: 'Welcome to HoopsIQ. Ask me anything about NBA players, eras, or playoff simulations.',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [goatMode, setGoatMode]   = useState(false);
  const [simMode, setSimMode]     = useState(false);
  const bottomRef                 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text?: string) => {
    const query = text || input.trim();
    if (!query || loading) return;
    setInput('');

    const ts = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { role: 'user', text: query, timestamp: ts }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: query })
      });
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'ai',
        text: data.response || data.error || 'No response received.',
        intent: data.intent,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'ai',
        text: 'Connection error. Check your API endpoint.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    } finally {
      setLoading(false);
    }
  };

  const intentColor = (intent?: string) => {
    switch (intent) {
      case 'goat':       return '#F5820A';
      case 'simulation': return '#7B8FD4';
      case 'era':        return '#2ECC71';
      default:           return '#8892A4';
    }
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 280px',
      height: 'calc(100vh - 48px)',
      background: '#0D0F14',
      color: '#F0EDE6'
    }}>

      {/* CHAT PANEL */}
      <div style={{ display: 'flex', flexDirection: 'column', borderRight: '0.5px solid rgba(255,255,255,0.08)' }}>

        {/* Header */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '0.5px solid rgba(255,255,255,0.08)',
          background: '#13161D',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#2ECC71', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontFamily: 'Courier New', fontSize: 11, letterSpacing: '0.12em', color: '#8892A4', textTransform: 'uppercase' }}>
              Live Session
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { label: 'GOAT Mode', active: goatMode, toggle: () => setGoatMode(!goatMode), color: '#C9A84C' },
              { label: 'Simulate',  active: simMode,  toggle: () => setSimMode(!simMode),   color: '#7B8FD4' }
            ].map(m => (
              <button key={m.label} onClick={m.toggle} style={{
                fontFamily: 'Courier New', fontSize: 9, padding: '3px 10px',
                borderRadius: 20, letterSpacing: '0.08em', textTransform: 'uppercase',
                border: `0.5px solid ${m.color}`,
                background: m.active ? `${m.color}22` : 'transparent',
                color: m.color, cursor: 'pointer'
              }}>{m.label}</button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '80%', padding: '10px 14px', borderRadius: 10,
                background: msg.role === 'user' ? 'rgba(245,130,10,0.12)' : '#1C2030',
                border: `0.5px solid ${msg.role === 'user' ? 'rgba(245,130,10,0.3)' : 'rgba(255,255,255,0.08)'}`,
                fontSize: 13, lineHeight: 1.6, fontFamily: 'sans-serif', whiteSpace: 'pre-wrap'
              }}>
                {msg.text}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                {msg.intent && (
                  <span style={{
                    fontFamily: 'Courier New', fontSize: 9, letterSpacing: '0.08em',
                    color: intentColor(msg.intent), textTransform: 'uppercase'
                  }}>{msg.intent}</span>
                )}
                <span style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4' }}>{msg.timestamp}</span>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{
                padding: '10px 14px', borderRadius: 10,
                background: '#1C2030', border: '0.5px solid rgba(255,255,255,0.08)',
                fontSize: 13, fontFamily: 'sans-serif', color: '#8892A4'
              }}>
                Analyzing
                <span style={{ display: 'inline-block', animation: 'blink 1s infinite' }}>...</span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Suggested prompts */}
        {messages.length <= 1 && (
          <div style={{ padding: '8px 20px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SUGGESTED.map(s => (
              <button key={s} onClick={() => sendMessage(s)} style={{
                fontFamily: 'sans-serif', fontSize: 11, padding: '5px 10px',
                borderRadius: 6, border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'transparent', color: '#8892A4', cursor: 'pointer'
              }}>{s}</button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{
          borderTop: '0.5px solid rgba(255,255,255,0.08)',
          padding: '12px 16px', display: 'flex', gap: 8, background: '#13161D'
        }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about any player, era, or matchup..."
            style={{
              flex: 1, background: '#1C2030',
              border: '0.5px solid rgba(255,255,255,0.12)',
              borderRadius: 8, padding: '9px 14px',
              color: '#F0EDE6', fontSize: 13, fontFamily: 'sans-serif', outline: 'none'
            }}
          />
          <button onClick={() => sendMessage()} disabled={loading} style={{
            background: loading ? '#333' : '#F5820A',
            border: 'none', borderRadius: 8, width: 36, height: 36,
            color: '#fff', fontSize: 16, cursor: loading ? 'not-allowed' : 'pointer'
          }}>›</button>
        </div>
      </div>

      {/* RIGHT PANEL — Stats */}
      <div style={{ background: '#13161D', padding: 16, overflowY: 'auto' }}>
        <div style={{ fontFamily: 'Courier New', fontSize: 9, letterSpacing: '0.12em', color: '#8892A4', textTransform: 'uppercase', marginBottom: 14 }}>
          Quick Stats
        </div>

        {[
          { name: 'Michael Jordan', era: '1984–2003 · SG', ppg: '31.4', ws: '214.0', score: 96.2, color: '#F5820A' },
          { name: 'LeBron James',   era: '2003–present · SF', ppg: '28.7', ws: '261.3', score: 93.8, color: '#7B8FD4' }
        ].map(p => (
          <div key={p.name} style={{
            background: '#1C2030', border: '0.5px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: 12, marginBottom: 10
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'Georgia, serif', marginBottom: 2 }}>{p.name}</div>
            <div style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4', marginBottom: 8 }}>{p.era}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: '#8892A4', fontFamily: 'sans-serif' }}>PPG (era-adj)</span>
              <span style={{ fontFamily: 'Courier New', fontSize: 11 }}>{p.ppg}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: '#8892A4', fontFamily: 'sans-serif' }}>Win Shares</span>
              <span style={{ fontFamily: 'Courier New', fontSize: 11 }}>{p.ws}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: '#8892A4', fontFamily: 'sans-serif' }}>GOAT Index</span>
              <span style={{ fontFamily: 'Courier New', fontSize: 11, color: p.color }}>{p.score}</span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
              <div style={{ height: '100%', width: `${p.score}%`, background: p.color, borderRadius: 2 }} />
            </div>
          </div>
        ))}

        <div style={{ marginTop: 16, fontFamily: 'Courier New', fontSize: 9, letterSpacing: '0.12em', color: '#8892A4', textTransform: 'uppercase', marginBottom: 10 }}>
          API Status
        </div>
        {['/chat', '/simulate', '/era'].map(route => (
          <div key={route} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontFamily: 'Courier New', fontSize: 10, color: '#8892A4' }}>{route}</span>
            <span style={{ fontFamily: 'Courier New', fontSize: 10, color: '#2ECC71' }}>● live</span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
      `}</style>
    </div>
  );
};

export default Dashboard;
